import { Copy, ExternalLink, FileText, HardDrive, GitBranch, Layers3 } from 'lucide-react'
import type { SearchResult, SearchResponse } from '@agentwiki/shared'
import type { SearchSource, SearchType } from '../../hooks/use-search'
import { API_BASE } from '../../lib/api-client'
import { buildSearchResultPresentation } from '../../lib/document-result-presentation'
import { documentReadPath } from '../../lib/document-route'
import { cn } from '../../lib/utils'
import { DocumentResultTerms } from '../shared/document-result-metadata'

interface SearchResultRowProps {
  result: SearchResult
  index: number
  searchId?: SearchResponse['searchId']
  searchType: SearchType
  source: SearchSource
  isDark: boolean
  onOpen: (
    result: SearchResult,
    position: number,
    searchId?: string,
    navigation?: 'current' | 'new-tab',
  ) => void
}

function FileResultMetadata({ result, isDark }: { result: SearchResult; isDark: boolean }) {
  return (
    <span className="aw-search-result-file-metadata">
      <span className={cn('aw-search-result-label', isDark && 'is-dark')}>File result</span>
      {result.sourcePath && <span>{result.sourcePath}</span>}
      {result.fileUrl && (
        <span>
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          Opens indexed file
        </span>
      )}
    </span>
  )
}

export function SearchResultRow({ result, index, searchId, isDark, onOpen }: SearchResultRowProps) {
  const presentation = buildSearchResultPresentation(result)
  const fileResult = presentation.kind === 'file'
  const Icon = fileResult ? HardDrive : FileText
  const href = fileResult
    ? result.fileUrl
      ? `${API_BASE}${result.fileUrl}`
      : '#'
    : documentReadPath({ id: result.documentId ?? result.id, slug: result.slug })

  const content = (
    <span className="flex min-w-0 gap-3">
      <span
        className={cn(
          'mt-0.5 rounded-lg p-2',
          isDark ? 'bg-surface-3 text-neutral-300' : 'bg-neutral-100 text-neutral-700',
        )}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-start justify-between gap-3">
          <span
            className={cn(
              'truncate text-sm font-semibold',
              isDark ? 'text-neutral-100' : 'text-neutral-900',
            )}
          >
            {presentation.title}
          </span>
        </span>
        <span
          className={cn(
            'mt-1 line-clamp-2 text-xs leading-5',
            isDark ? 'text-neutral-400' : 'text-neutral-600',
          )}
        >
          {presentation.excerpt || (fileResult ? 'Indexed file result' : 'No excerpt available')}
        </span>
        {fileResult ? (
          <FileResultMetadata result={result} isDark={isDark} />
        ) : (
          <span className="mt-3 grid gap-2">
            <DocumentResultTerms presentation={presentation} />
            <span className="aw-knowledge-row-meta">
              <span>
                <GitBranch className="h-3.5 w-3.5" aria-hidden="true" />
                {presentation.source.label}
              </span>
              <span>
                <Layers3 className="h-3.5 w-3.5" aria-hidden="true" />
                {presentation.memoryClassLabel ?? 'No memory class'}
              </span>
            </span>
          </span>
        )}
      </span>
    </span>
  )

  return (
    <article
      data-return-focus-id={result.documentId ?? result.id}
      tabIndex={-1}
      className={cn(
        'aw-search-result rounded-lg border p-4 transition-colors',
        isDark
          ? 'border-white/[0.06] bg-surface-1 hover:border-brand-500/40 hover:bg-surface-2'
          : 'border-neutral-200 bg-white hover:border-brand-300 hover:bg-neutral-50',
      )}
    >
      <a
        href={href}
        target={fileResult ? '_blank' : undefined}
        rel={fileResult ? 'noopener noreferrer' : undefined}
        className="block text-left"
        onClick={
          fileResult
            ? undefined
            : (event) => {
                if (
                  event.button !== 0 ||
                  event.metaKey ||
                  event.ctrlKey ||
                  event.shiftKey ||
                  event.altKey
                ) {
                  return
                }
                event.preventDefault()
                onOpen(result, index + 1, searchId)
              }
        }
      >
        {content}
      </a>
      {!fileResult && (
        <span className="aw-search-result-actions">
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${presentation.title} in new tab`}
            onClick={() => onOpen(result, index + 1, searchId, 'new-tab')}
          >
            <ExternalLink aria-hidden="true" />
          </a>
          <button
            type="button"
            aria-label={`Copy link to ${presentation.title}`}
            onClick={() =>
              void navigator.clipboard?.writeText(new URL(href, window.location.href).href)
            }
          >
            <Copy aria-hidden="true" />
          </button>
        </span>
      )}
    </article>
  )
}
